import clsx from "clsx"
import type { ReactNode } from "react"

import { STATE_COLUMN_ORDER, type Trend } from "@/types/sprint-report"

interface Props {
  trend: Trend
}

/** "2026-06-15" → "Mon Jun 15" / short "Mon 15" */
function dayLabel(iso: string, short = false): string {
  const d = new Date(`${iso}T00:00:00Z`)
  return d.toLocaleDateString("en-CA", {
    weekday: "short",
    ...(short ? { day: "numeric" } : { month: "short", day: "numeric" }),
    timeZone: "UTC",
  })
}

export function StatusTrend({ trend }: Props) {
  const { days } = trend
  if (!days || days.length === 0) {
    return <p className="p-6 text-center text-sm text-slate-500">No trend data yet.</p>
  }

  const openByDate = new Map(trend.open_total.map((p) => [p.date, p.count]))
  const flowByDate = new Map(trend.flow.map((p) => [p.date, p]))
  const stateByDate = new Map(trend.per_state.map((p) => [p.date, p.by_state]))
  const maxOpen = Math.max(1, ...trend.open_total.map((p) => p.count))
  const maxFlow = Math.max(1, ...trend.flow.flatMap((f) => [f.inflow, f.outflow]))

  const openNow = openByDate.get(days[days.length - 1]) ?? 0
  const openStart = openByDate.get(days[0]) ?? 0
  const openDelta = openNow - openStart
  const resolvedWeek = trend.flow.reduce((s, f) => s + f.outflow, 0)
  const addedWeek = trend.flow.reduce((s, f) => s + f.inflow, 0)
  const netWeek = addedWeek - resolvedWeek

  // State rows: known order first, then any extras (e.g. "other").
  const known = STATE_COLUMN_ORDER.map((c) => c.key)
  const extraKeys = [...new Set(trend.per_state.flatMap((p) => Object.keys(p.by_state)))].filter(
    (k) => !known.includes(k as never),
  )
  const stateRows = [
    ...STATE_COLUMN_ORDER.map((c) => ({ key: c.key as string, label: c.label })),
    ...extraKeys.map((k) => ({ key: k, label: k })),
  ]

  return (
    <div className="space-y-8">
      {/* ── Hero KPIs ─────────────────────────────────────────── */}
      <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi
          className="animate-rise"
          label="Open now"
          value={openNow}
          tone={openDelta <= 0 ? "win" : "warn"}
          foot={
            <DeltaTag value={openDelta} goodWhenNegative />
          }
        />
        <Kpi
          className="animate-rise animate-delay-1"
          label="Resolved this week"
          value={resolvedWeek}
          tone="win"
          star={resolvedWeek > 0}
          foot={<span className="text-win-deep/70">items closed ✓</span>}
        />
        <Kpi
          className="animate-rise animate-delay-2"
          label="Added this week"
          value={addedWeek}
          tone="warn"
          foot={<span className="text-amber-700/70">new arrivals</span>}
        />
        <Kpi
          className="animate-rise animate-delay-3"
          label="Net this week"
          value={`${netWeek > 0 ? "+" : ""}${netWeek}`}
          tone={netWeek <= 0 ? "win" : "warn"}
          star={netWeek < 0}
          foot={
            netWeek <= 0 ? (
              <span className="text-win-deep/80">closing faster than opening</span>
            ) : (
              <span className="text-amber-700/80">backlog growing</span>
            )
          }
        />
      </section>

      {/* ── Open burndown ─────────────────────────────────────── */}
      <section>
        <SectionTitle>Open R1 items · daily</SectionTitle>
        <div className="space-y-1.5">
          {days.map((d, i) => {
            const count = openByDate.get(d) ?? 0
            const prev = i > 0 ? openByDate.get(days[i - 1]) ?? 0 : null
            const delta = prev === null ? null : count - prev
            const isLast = i === days.length - 1
            return (
              <div key={d} className="flex items-center gap-3 text-xs">
                <span
                  className={clsx(
                    "num w-24 shrink-0",
                    isLast ? "font-semibold text-ink" : "text-slate-500",
                  )}
                >
                  {dayLabel(d)}
                </span>
                <div className="h-5 flex-1 overflow-hidden rounded-md bg-slate-100">
                  <div
                    className={clsx(
                      "h-5 origin-left rounded-md animate-growx",
                      isLast
                        ? "bg-gradient-to-r from-header to-headerSub shadow-card"
                        : "bg-gradient-to-r from-header/70 to-headerSub/70",
                    )}
                    style={{ width: `${(count / maxOpen) * 100}%` }}
                  />
                </div>
                {delta !== null && <DeltaTag value={delta} goodWhenNegative compact />}
                <span className="num w-9 shrink-0 text-right text-sm font-bold text-ink">
                  {count}
                </span>
              </div>
            )
          })}
        </div>
      </section>

      {/* ── Inflow vs Outflow ─────────────────────────────────── */}
      <section>
        <SectionTitle>
          Inflow vs Outflow{" "}
          <span className="ml-1 text-[10px] font-normal normal-case tracking-normal text-slate-400">
            ← resolved · added →
          </span>
        </SectionTitle>
        <div className="space-y-1.5">
          {days.map((d) => {
            const f = flowByDate.get(d) ?? { inflow: 0, outflow: 0 }
            const net = f.inflow - f.outflow
            const win = net <= 0
            return (
              <div key={d} className="flex items-center gap-3 text-xs">
                <span className="num w-24 shrink-0 text-slate-500">{dayLabel(d)}</span>
                {/* diverging bar: resolved (green) left of center, added (amber) right */}
                <div className="flex flex-1 items-center">
                  <div className="flex flex-1 justify-end">
                    <div
                      className="h-4 rounded-l-md bg-gradient-to-l from-win to-win/60"
                      style={{ width: `${(f.outflow / maxFlow) * 100}%` }}
                    />
                  </div>
                  <div className="z-10 h-5 w-px bg-slate-300" />
                  <div className="flex flex-1 justify-start">
                    <div
                      className="h-4 rounded-r-md bg-gradient-to-r from-warn to-warn/60"
                      style={{ width: `${(f.inflow / maxFlow) * 100}%` }}
                    />
                  </div>
                </div>
                <span className="num w-10 shrink-0 text-right font-semibold text-win-deep">
                  {f.outflow ? `−${f.outflow}` : "·"}
                </span>
                <span className="num w-10 shrink-0 text-right font-semibold text-warn">
                  {f.inflow ? `+${f.inflow}` : "·"}
                </span>
                <span
                  className={clsx(
                    "num inline-flex w-12 shrink-0 justify-center rounded-full px-1.5 py-0.5 text-[11px] font-bold ring-1",
                    win
                      ? "bg-win-light text-win-deep ring-win/30"
                      : "bg-warn-light text-amber-800 ring-warn/30",
                  )}
                  title="Net (added − resolved)"
                >
                  {net < 0 && <span className="mr-0.5">▼</span>}
                  {net > 0 ? `+${net}` : net}
                </span>
              </div>
            )
          })}
        </div>
      </section>

      {/* ── Per-state ─────────────────────────────────────────── */}
      <section>
        <SectionTitle>Open items per state</SectionTitle>
        <div className="overflow-x-auto rounded-xl border border-slate-200/80">
          <table className="data-table">
            <thead>
              <tr>
                <th>State</th>
                {days.map((d) => (
                  <th key={d} className="text-right">
                    {dayLabel(d, true)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stateRows.map((row) => {
                const counts = days.map((d) => (stateByDate.get(d) ?? {})[row.key] ?? 0)
                if (counts.every((c) => c === 0)) return null
                return (
                  <tr key={row.key}>
                    <td className="text-slate-600">{row.label}</td>
                    {counts.map((c, i) => (
                      <td key={i} className="num text-right">
                        {c === 0 ? <span className="text-slate-300">·</span> : c}
                      </td>
                    ))}
                  </tr>
                )
              })}
              <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                <td className="text-ink">Total open</td>
                {days.map((d) => (
                  <td key={d} className="num text-right text-ink">
                    {openByDate.get(d) ?? 0}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h2 className="mb-2.5 flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wide text-ink">
      <span className="h-3.5 w-1 rounded-full bg-gradient-to-b from-headerSub to-win" />
      {children}
    </h2>
  )
}

function DeltaTag({
  value,
  goodWhenNegative = false,
  compact = false,
}: {
  value: number
  goodWhenNegative?: boolean
  compact?: boolean
}) {
  if (value === 0) {
    return <span className="num text-[11px] text-slate-400">{compact ? "—" : "no change"}</span>
  }
  const good = goodWhenNegative ? value < 0 : value > 0
  return (
    <span
      className={clsx(
        "num inline-flex items-center gap-0.5 text-[11px] font-semibold",
        good ? "text-win-deep" : "text-amber-700",
      )}
    >
      {value < 0 ? "▼" : "▲"}
      {Math.abs(value)}
    </span>
  )
}

type Tone = "win" | "warn" | "neutral"

function Kpi({
  label,
  value,
  tone,
  foot,
  star = false,
  className,
}: {
  label: string
  value: number | string
  tone: Tone
  foot?: ReactNode
  star?: boolean
  className?: string
}) {
  const toneRing =
    tone === "win"
      ? "ring-win/25 bg-win-light/50"
      : tone === "warn"
        ? "ring-warn/25 bg-warn-light/40"
        : "ring-slate-200 bg-white/70"
  const toneText = tone === "win" ? "text-win-deep" : tone === "warn" ? "text-amber-700" : "text-ink"
  return (
    <div
      className={clsx(
        "relative overflow-hidden rounded-2xl px-4 py-3 ring-1 shadow-card",
        toneRing,
        star && "animate-winpulse",
        className,
      )}
    >
      {star && (
        <span className="absolute -right-6 -top-6 h-16 w-16 rounded-full bg-win/20 blur-xl" />
      )}
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={clsx("font-display text-4xl font-extrabold leading-none", toneText)}>
        {value}
      </div>
      {foot && <div className="mt-1 text-[11px] font-medium">{foot}</div>}
    </div>
  )
}
